import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import {
  HabitaEmptyState,
  HabitaErrorNotice,
  classNames,
  habitaSurfaceClass,
} from '../../../shared/components/habita';
import type {
  CommercialSdrRankingIndicator,
  CommercialSdrRankingItem,
  CommercialSdrRankingResponse,
} from '../../../shared/models';

interface PerformanceRankingTabProps {
  data: CommercialSdrRankingResponse | null;
  error: string;
  indicator: CommercialSdrRankingIndicator;
  periodLoading: boolean;
  onRefresh: () => void;
}

export interface PerformanceRankingTabHandle {
  enterTvMode: () => void;
}

const TV_REFRESH_INTERVAL_MS = 5 * 60 * 1000;
const TV_HINT_DURATION_MS = 4500;

export interface CommercialRankingIndicatorCopy {
  columnLabel: string;
  emptyPeriodDescription: string;
  emptyPeriodTitle: string;
  emptyTodayDescription: string;
  emptyTodayTitle: string;
  icon: string;
  label: string;
  lastEventLabel: string;
  pageDescription: string;
  pageTitle: string;
  plural: string;
  singular: string;
  topThreeDescription: string;
}

export const commercialRankingIndicatorOptions: Array<{
  label: string;
  value: CommercialSdrRankingIndicator;
}> = [
  { label: 'Pastas geradas', value: 'generated_folders' },
  { label: 'Pastas aprovadas', value: 'approved_folders' },
  { label: 'Visitas agendadas', value: 'scheduled_visits' },
];

export const commercialRankingIndicatorCopy: Record<
  CommercialSdrRankingIndicator,
  CommercialRankingIndicatorCopy
> = {
  approved_folders: {
    columnLabel: 'Pastas aprovadas',
    emptyPeriodDescription: 'O pódio será exibido assim que houver pastas aprovadas para os filtros selecionados.',
    emptyPeriodTitle: 'Sem aprovações no período',
    emptyTodayDescription: 'O SDR líder aparecerá aqui assim que a primeira pasta do dia for aprovada.',
    emptyTodayTitle: 'Nenhuma aprovação registrada hoje',
    icon: 'ri-calendar-check-line',
    label: 'Pastas aprovadas',
    lastEventLabel: 'Última aprovação',
    pageDescription: 'Desempenho dos SDRs na aprovação de pastas de crédito imobiliário, integrado ao CRM.',
    pageTitle: 'Ranking de Pastas Aprovadas',
    plural: 'pastas aprovadas',
    singular: 'pasta aprovada',
    topThreeDescription: 'Os SDRs com mais pastas aprovadas no intervalo selecionado',
  },
  generated_folders: {
    columnLabel: 'Pastas geradas',
    emptyPeriodDescription: 'O pódio será exibido assim que houver pastas geradas para os filtros selecionados.',
    emptyPeriodTitle: 'Sem pastas geradas no período',
    emptyTodayDescription: 'O SDR líder aparecerá aqui assim que a primeira pasta do dia for gerada.',
    emptyTodayTitle: 'Nenhuma pasta gerada hoje',
    icon: 'ri-folder-add-line',
    label: 'Pastas geradas',
    lastEventLabel: 'Última pasta gerada',
    pageDescription: 'Desempenho dos SDRs na geração de pastas de crédito imobiliário, integrado ao CRM.',
    pageTitle: 'Ranking de Pastas Geradas',
    plural: 'pastas geradas',
    singular: 'pasta gerada',
    topThreeDescription: 'Os SDRs com mais pastas geradas no intervalo selecionado',
  },
  scheduled_visits: {
    columnLabel: 'Visitas agendadas',
    emptyPeriodDescription: 'O pódio será exibido assim que houver visitas agendadas para os filtros selecionados.',
    emptyPeriodTitle: 'Sem visitas agendadas no período',
    emptyTodayDescription: 'O SDR líder aparecerá aqui assim que a primeira visita do dia for agendada.',
    emptyTodayTitle: 'Nenhuma visita agendada hoje',
    icon: 'ri-calendar-event-line',
    label: 'Visitas agendadas',
    lastEventLabel: 'Última visita agendada',
    pageDescription: 'Desempenho dos SDRs no agendamento de visitas, integrado ao CRM.',
    pageTitle: 'Ranking de Visitas Agendadas',
    plural: 'visitas agendadas',
    singular: 'visita agendada',
    topThreeDescription: 'Os SDRs com mais visitas agendadas no intervalo selecionado',
  },
};

const podiumStyleByRank: Record<number, {
  avatarClass: string;
  icon: string;
  label: string;
  pedestalClass: string;
  pedestalHeightClass: string;
  tvPedestalHeightClass: string;
}> = {
  1: {
    avatarClass: 'border-amber-100 bg-amber-400 text-[#173b26]',
    icon: 'ri-trophy-fill',
    label: '1º lugar',
    pedestalClass: 'bg-gradient-to-b from-amber-400 to-amber-500 text-[#173b26]',
    pedestalHeightClass: 'h-36',
    tvPedestalHeightClass: 'h-52',
  },
  2: {
    avatarClass: 'border-white bg-[#275b3a] text-white',
    icon: 'ri-medal-fill',
    label: '2º lugar',
    pedestalClass: 'bg-gradient-to-b from-[#aeb6a6] to-[#8d9585] text-white',
    pedestalHeightClass: 'h-32',
    tvPedestalHeightClass: 'h-44',
  },
  3: {
    avatarClass: 'border-white bg-[#275b3a] text-white',
    icon: 'ri-medal-fill',
    label: '3º lugar',
    pedestalClass: 'bg-gradient-to-b from-[#b98255] to-[#93643c] text-white',
    pedestalHeightClass: 'h-28',
    tvPedestalHeightClass: 'h-36',
  },
};

function formatNumber(value: number): string {
  return value.toLocaleString('pt-BR');
}

function formatDate(value: string): string {
  if (!value) return '-';
  const date = new Date(`${value}T12:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('pt-BR');
}

function formatLongDate(value: string): string {
  if (!value) return '-';
  const date = new Date(`${value}T12:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
}

function formatUpdatedAt(value?: string | null): string {
  if (!value) return 'Atualização ainda não registrada';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Atualização ainda não registrada';
  return `Atualizado em ${date.toLocaleDateString('pt-BR')} às ${date.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
}

function formatEventAt(value: string | null, label: string): string {
  if (!value) return `${label} não informada`;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return `${label} não informada`;
  return `${label}: ${date.toLocaleDateString('pt-BR')} às ${date.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'SD';
  const selected = parts.length === 1 ? parts : [parts[0], parts.at(-1) ?? ''];
  return selected.map((part) => part.charAt(0)).join('').toLocaleUpperCase('pt-BR').slice(0, 2);
}

function LoadingBlock({ className }: { className: string }) {
  return <div className={classNames('animate-pulse rounded-lg bg-muted/70', className)} />;
}

function SdrAvatar({
  className,
  name,
}: {
  className?: string;
  name: string;
}) {
  return (
    <span
      aria-label={`Iniciais de ${name}`}
      className={classNames(
        'flex shrink-0 items-center justify-center rounded-full border-[3px] font-instrument font-bold',
        className
      )}
    >
      {initials(name)}
    </span>
  );
}

function TrendIndicator({
  compact = false,
  item,
  large = false,
}: {
  compact?: boolean;
  item: CommercialSdrRankingItem;
  large?: boolean;
}) {
  const isUp = item.variation > 0;
  const isDown = item.variation < 0;
  const label = item.variation === 0
    ? 'Estável'
    : `${isUp ? '+' : ''}${formatNumber(item.variation)}`;

  return (
    <span
      className={classNames(
        'inline-flex items-center gap-1 font-instrument font-semibold',
        compact
          ? classNames('justify-end', large ? 'text-base' : 'text-xs')
          : classNames('rounded-full px-2 py-1', large ? 'text-base' : 'text-xs'),
        isUp && (compact
          ? 'text-emerald-700 dark:text-emerald-300'
          : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300'),
        isDown && (compact
          ? 'text-red-700 dark:text-red-300'
          : 'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300'),
        !isUp && !isDown && (compact
          ? 'text-textSecondary'
          : 'bg-slate-100 text-slate-600 dark:bg-slate-500/15 dark:text-slate-300')
      )}
      aria-label={`${formatNumber(item.count)} no período atual; ${formatNumber(item.previous_count)} no período anterior`}
    >
      <i
        className={isUp ? 'ri-arrow-up-line' : isDown ? 'ri-arrow-down-line' : 'ri-subtract-line'}
        aria-hidden
      />
      {label}
    </span>
  );
}

function PodiumCard({
  copy,
  item,
  tvMode,
}: {
  copy: CommercialRankingIndicatorCopy;
  item: CommercialSdrRankingItem;
  tvMode: boolean;
}) {
  const style = podiumStyleByRank[item.rank] ?? podiumStyleByRank[3];
  const isLeader = item.rank === 1;

  return (
    <article
      className={classNames(
        'relative flex min-w-0 flex-col items-center justify-end px-2 pt-6 text-center',
        isLeader && 'order-first md:order-none',
        tvMode && 'px-6 pt-8'
      )}
    >
      <span className={classNames(
        'mb-3 inline-flex items-center gap-2 font-instrument text-sm font-extrabold uppercase tracking-wide text-amber-700 dark:text-amber-400',
        tvMode && 'gap-3 text-xl'
      )}>
        <i className={classNames(style.icon, tvMode ? 'text-3xl' : 'text-xl')} aria-hidden />
        {style.label}
      </span>
      <SdrAvatar
        className={classNames(
          'h-16 w-16 text-xl shadow-[0_4px_14px_rgba(13,53,18,0.18)]',
          style.avatarClass,
          isLeader && 'h-20 w-20 text-2xl',
          tvMode && (isLeader ? 'h-28 w-28 text-4xl' : 'h-24 w-24 text-3xl')
        )}
        name={item.sdr}
      />
      <h3 className={classNames(
        'mt-3 line-clamp-2 max-w-full font-instrument text-lg font-extrabold leading-tight text-textPrimary',
        isLeader && 'text-xl',
        tvMode && (isLeader ? 'text-4xl' : 'text-3xl')
      )}>
        {item.sdr}
      </h3>
      <div className={classNames(
        'mt-5 hidden w-full flex-col items-center justify-center rounded-t-lg px-3 font-instrument shadow-[inset_0_1px_0_rgba(255,255,255,0.28)] md:flex',
        style.pedestalClass,
        tvMode ? style.tvPedestalHeightClass : style.pedestalHeightClass,
        tvMode && 'px-5'
      )}>
        <strong className={classNames(
          'font-instrument text-4xl font-extrabold leading-none tabular-nums',
          isLeader && 'text-5xl',
          tvMode && (isLeader ? 'text-7xl' : 'text-6xl')
        )}>
          {formatNumber(item.count)}
        </strong>
        <span className={classNames(
          'mt-2 font-instrument text-xs font-bold uppercase tracking-wide opacity-85',
          tvMode && 'mt-3 text-base'
        )}>
          {item.count === 1 ? copy.singular : copy.plural}
        </span>
        <span className={classNames(
          'mt-1 font-instrument text-[10px] font-medium opacity-75',
          tvMode && 'mt-2 text-sm'
        )}>
          {formatEventAt(item.last_event_at, copy.lastEventLabel)}
        </span>
      </div>
      <div className={classNames(
        'mt-4 flex items-baseline gap-1.5 font-instrument font-extrabold md:hidden',
        isLeader ? 'text-amber-600' : 'text-[#173b26]'
      )}>
        <span className="text-4xl tabular-nums">{formatNumber(item.count)}</span>
        <span className="text-xs uppercase tracking-wide">
          {item.count === 1 ? copy.singular : copy.plural}
        </span>
      </div>
      <p className="mt-1 font-instrument text-[10px] text-textSecondary md:hidden">
        {formatEventAt(item.last_event_at, copy.lastEventLabel)}
      </p>
    </article>
  );
}

function RankingList({
  copy,
  items,
  tvMode,
}: {
  copy: CommercialRankingIndicatorCopy;
  items: CommercialSdrRankingItem[];
  tvMode: boolean;
}) {
  return (
    <div className="overflow-hidden rounded-lg border border-border-subtle bg-card">
      <div className={classNames(
        'hidden border-b border-border-subtle font-instrument font-bold uppercase tracking-wide text-textSecondary sm:grid',
        tvMode
          ? 'grid-cols-[64px,minmax(0,1fr),200px,150px] gap-5 px-7 py-4 text-sm'
          : 'grid-cols-[48px,minmax(0,1fr),150px,110px] gap-3 px-5 py-3 text-[11px]'
      )}>
        <span>#</span>
        <span>SDR</span>
        <span className="text-right">{copy.columnLabel}</span>
        <span className="text-right">Variação</span>
      </div>
      {items.map((item) => (
        <article
          key={item.sdr}
          className={classNames(
            'grid grid-cols-[auto,minmax(0,1fr),auto] items-center border-b border-border-subtle transition-colors last:border-b-0 hover:bg-muted/35',
            tvMode
              ? 'gap-5 px-7 py-5 sm:grid-cols-[64px,minmax(0,1fr),200px,150px]'
              : 'gap-3 px-4 py-3 sm:grid-cols-[48px,minmax(0,1fr),150px,110px] sm:px-5'
          )}
        >
          <span className={classNames(
            'flex items-center justify-center rounded-full border border-border-subtle bg-background font-instrument font-bold text-textSecondary',
            tvMode ? 'h-12 w-12 text-lg' : 'h-8 w-8 text-xs'
          )}>
            {item.rank}
          </span>

          <div className="flex min-w-0 items-center gap-3">
            <SdrAvatar
              className={classNames(
                'bg-[#275b3a] text-white',
                tvMode ? 'h-14 w-14 text-lg' : 'h-10 w-10 text-sm'
              )}
              name={item.sdr}
            />
            <div className="min-w-0">
              <p className={classNames(
                'truncate font-instrument font-semibold text-textPrimary',
                tvMode ? 'text-xl' : 'text-sm'
              )}>
                {item.sdr}
              </p>
              <p className={classNames(
                'mt-0.5 truncate font-instrument text-textSecondary',
                tvMode ? 'text-sm' : 'text-[10px]'
              )}>
                {formatEventAt(item.last_event_at, copy.lastEventLabel)}
              </p>
            </div>
          </div>

          <div className="text-right">
            <p className={classNames(
              'font-instrument font-bold tabular-nums text-textPrimary',
              tvMode ? 'text-3xl' : 'text-lg'
            )}>
              {formatNumber(item.count)}
              <span className={classNames(
                'ml-1 font-instrument font-semibold text-textSecondary',
                tvMode ? 'text-sm' : 'text-[10px]'
              )}>
                {item.count === 1 ? copy.singular : copy.plural}
              </span>
            </p>
          </div>

          <div className="col-start-2 flex justify-start sm:col-auto sm:justify-end">
            <TrendIndicator compact item={item} large={tvMode} />
          </div>
        </article>
      ))}
    </div>
  );
}

const PerformanceRankingTab = forwardRef<
  PerformanceRankingTabHandle,
  PerformanceRankingTabProps
>(function PerformanceRankingTab({
  data,
  error,
  indicator,
  periodLoading,
  onRefresh,
}, ref) {
  const onRefreshRef = useRef(onRefresh);
  const [showTvHint, setShowTvHint] = useState(false);
  const [tvMode, setTvMode] = useState(false);

  const copy = commercialRankingIndicatorCopy[indicator];
  const rankingData = data?.indicator === indicator ? data : null;
  const periodRanking = rankingData?.items ?? [];
  const podium = periodRanking.slice(0, 3);
  const remainingRanking = periodRanking.slice(3);
  const todayLeader = rankingData?.today.items[0] ?? null;
  const podiumDisplay = podium.length <= 1
    ? podium
    : [podium[1], podium[0], ...podium.slice(2)];

  useEffect(() => {
    onRefreshRef.current = onRefresh;
  }, [onRefresh]);

  useEffect(() => {
    function handleFullscreenChange() {
      if (!document.fullscreenElement) setTvMode(false);
    }

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  useEffect(() => {
    if (!tvMode) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const hintTimer = window.setTimeout(() => setShowTvHint(false), TV_HINT_DURATION_MS);
    const refreshTimer = window.setInterval(() => onRefreshRef.current(), TV_REFRESH_INTERVAL_MS);

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key !== 'Escape') return;
      setTvMode(false);
      if (document.fullscreenElement) void document.exitFullscreen();
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.clearInterval(refreshTimer);
      window.clearTimeout(hintTimer);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [tvMode]);

  async function enterTvMode() {
    try {
      await document.documentElement.requestFullscreen?.();
    } catch {
      // The body portal remains as the fallback when native fullscreen is unavailable.
    }
    setShowTvHint(true);
    setTvMode(true);
  }

  useImperativeHandle(ref, () => ({
    enterTvMode: () => {
      void enterTvMode();
    },
  }));

  const podiumGridClass = podium.length === 1
    ? 'mx-auto max-w-[280px] md:grid-cols-1'
    : podium.length === 2
      ? 'mx-auto max-w-[560px] md:grid-cols-2'
      : 'mx-auto max-w-[820px] md:grid-cols-3';

  const content = (
    <section
      className={classNames(
        'space-y-8',
        tvMode && 'fixed inset-0 z-[200] min-h-screen overflow-y-auto bg-background p-6 text-textPrimary sm:p-8 xl:p-10'
      )}
    >
      {showTvHint && tvMode && (
        <div className="fixed left-1/2 top-5 z-[210] -translate-x-1/2 rounded-md bg-black/80 px-4 py-2 font-instrument text-sm font-medium text-white shadow-lg">
          Pressione ESC para sair
        </div>
      )}

      {error.trim() && <HabitaErrorNotice>{error}</HabitaErrorNotice>}

      {periodLoading && !rankingData ? (
        <LoadingBlock className={classNames('h-48', tvMode && 'h-64')} />
      ) : rankingData ? (
        <section
          className="overflow-hidden rounded-[18px] bg-[#0d3512] text-white"
          style={{
            backgroundImage: [
              'radial-gradient(rgba(240, 166, 58, 0.088) 1px, transparent 1.4px)',
              'linear-gradient(135deg, #0d3512 0%, #154420 55%, #1f5126 130%)',
            ].join(', '),
            backgroundSize: '16px 16px, auto',
          }}
        >
          {todayLeader ? (
            <div className={classNames(
              'grid gap-7 px-7 py-9 lg:grid-cols-[auto,minmax(0,1fr),auto] lg:items-center',
              tvMode && 'px-14 py-14'
            )}>
              <SdrAvatar
                className={classNames(
                  'h-20 w-20 border-white/25 bg-[linear-gradient(150deg,#f4b655,#f0a63a)] text-2xl text-[#0d3512] shadow-[0_8px_20px_rgba(0,0,0,0.28)]',
                  tvMode && 'h-32 w-32 text-5xl'
                )}
                name={todayLeader.sdr}
              />

              <div className="min-w-0">
                <p className={classNames(
                  'flex items-center gap-1.5 font-instrument text-xs font-bold uppercase tracking-widest text-[#f4b655]',
                  tvMode && 'text-base'
                )}>
                  <i className="ri-star-fill" aria-hidden />
                  Líder do dia
                </p>
                <h3 className={classNames(
                  'mt-2 break-words font-instrument text-4xl font-extrabold leading-tight text-white',
                  tvMode && 'text-6xl'
                )}>
                  {todayLeader.sdr}
                </h3>
                <div className="mt-4 flex flex-wrap items-center gap-3">
                  <TrendIndicator item={todayLeader} large={tvMode} />
                  <span className={classNames(
                    'font-instrument text-xs text-white/60',
                    tvMode && 'text-base'
                  )}>
                    em relação a {formatLongDate(rankingData.today.previous_date)}
                  </span>
                </div>
              </div>

              <div className="min-w-48 text-left lg:text-right">
                <p className={classNames(
                  'font-instrument text-7xl font-extrabold tabular-nums text-[#f0a63a]',
                  tvMode && 'text-9xl'
                )}>
                  {formatNumber(todayLeader.count)}
                </p>
                <p className={classNames(
                  'mt-1 font-instrument text-xs text-white/55',
                  tvMode && 'text-base'
                )}>
                  {formatEventAt(todayLeader.last_event_at, copy.lastEventLabel)}
                </p>
                <p className={classNames(
                  'mt-1 font-instrument text-sm font-semibold text-white/65',
                  tvMode && 'text-xl'
                )}>
                  {todayLeader.count === 1
                    ? `${copy.singular} no dia`
                    : `${copy.plural} no dia`}
                </p>
                <p className={classNames(
                  'mt-3 font-instrument text-[11px] text-white/45',
                  tvMode && 'text-sm'
                )}>
                  {formatUpdatedAt(rankingData.generated_at)}
                </p>
              </div>
            </div>
          ) : (
            <div className={classNames(
              'grid gap-6 px-7 py-9 sm:grid-cols-[auto,minmax(0,1fr),auto] sm:items-center',
              tvMode && 'px-14 py-14'
            )}>
              <div className={classNames(
                'flex h-20 w-20 items-center justify-center rounded-full border border-white/15 bg-white/10 text-3xl text-[#f4b655]',
                tvMode && 'h-32 w-32 text-5xl'
              )}>
                <i className={copy.icon} aria-hidden />
              </div>

              <div className="min-w-0">
                <p className={classNames(
                  'flex items-center gap-1.5 font-instrument text-xs font-bold uppercase tracking-widest text-[#f4b655]',
                  tvMode && 'text-base'
                )}>
                  <i className="ri-star-line" aria-hidden />
                  Líder do dia
                </p>
                <h3 className={classNames(
                  'mt-2 font-instrument text-3xl font-extrabold leading-tight text-white',
                  tvMode && 'text-5xl'
                )}>
                  {copy.emptyTodayTitle}
                </h3>
                <p className={classNames(
                  'mt-2 font-instrument text-sm text-white/60',
                  tvMode && 'text-lg'
                )}>
                  {copy.emptyTodayDescription}
                </p>
              </div>

              <div className="text-left sm:text-right">
                <p className={classNames(
                  'font-instrument text-7xl font-extrabold tabular-nums text-[#f0a63a]',
                  tvMode && 'text-9xl'
                )}>
                  0
                </p>
                <p className={classNames(
                  'font-instrument text-sm font-semibold text-white/65',
                  tvMode && 'text-xl'
                )}>
                  {copy.plural} no dia
                </p>
                <p className={classNames(
                  'mt-3 font-instrument text-[11px] text-white/45',
                  tvMode && 'text-sm'
                )}>
                  {formatUpdatedAt(rankingData.generated_at)}
                </p>
              </div>
            </div>
          )}
        </section>
      ) : null}

      <section>
        <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h2 className={classNames(
              'font-instrument text-xl font-extrabold text-textPrimary',
              tvMode && 'text-3xl'
            )}>
              Top 3 do período
            </h2>
            <p className={classNames(
              'mt-1 font-instrument text-sm text-textSecondary',
              tvMode && 'text-base'
            )}>
              {copy.topThreeDescription}
            </p>
          </div>
          <div className="text-left sm:text-right">
            <p className={classNames(
              'font-instrument text-xs font-semibold text-textSecondary',
              tvMode && 'text-base'
            )}>
              {formatDate(rankingData?.period.start_date ?? '')} a {formatDate(rankingData?.period.end_date ?? '')}
            </p>
            <p className={classNames(
              'mt-1 font-instrument text-[11px] text-textSecondary',
              tvMode && 'text-sm'
            )}>
              Total no período:{' '}
              <strong className="text-textPrimary">
                {formatNumber(rankingData?.total_count ?? 0)} {copy.plural}
              </strong>
            </p>
            {!todayLeader && (
              <p className="mt-1 font-instrument text-[11px] text-textSecondary">
                {formatUpdatedAt(rankingData?.generated_at)}
              </p>
            )}
            {(rankingData?.unassigned_count ?? 0) > 0 && (
              <p className="mt-1 font-instrument text-[11px] text-textSecondary">
                {formatNumber(rankingData?.unassigned_count ?? 0)} sem SDR fora do ranking
              </p>
            )}
          </div>
        </div>

        <div className={classNames(
          habitaSurfaceClass,
          'overflow-hidden px-4 pt-8 sm:px-8 sm:pt-10',
          tvMode && 'px-10 pt-12'
        )}>
          {periodLoading && !rankingData ? (
            <div className="grid gap-4 pb-8 md:grid-cols-3">
              <LoadingBlock className="h-56" />
              <LoadingBlock className="h-56" />
              <LoadingBlock className="h-56" />
            </div>
          ) : podium.length > 0 ? (
            <div className={classNames(
              'grid items-end gap-5',
              podiumGridClass,
              tvMode && 'gap-8'
            )}>
              {podiumDisplay.map((item) => (
                <PodiumCard copy={copy} key={item.sdr} item={item} tvMode={tvMode} />
              ))}
            </div>
          ) : (
            <div className="pb-8">
              <HabitaEmptyState
                icon="ri-trophy-line"
                title={copy.emptyPeriodTitle}
                description={copy.emptyPeriodDescription}
                className="border-0"
              />
            </div>
          )}
        </div>
      </section>

      {remainingRanking.length > 0 && (
        <section>
          <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h2 className={classNames(
                'font-instrument text-xl font-extrabold text-textPrimary',
                tvMode && 'text-3xl'
              )}>
                Demais SDRs
              </h2>
              <p className={classNames(
                'mt-1 font-instrument text-sm text-textSecondary',
                tvMode && 'text-base'
              )}>
                Classificação a partir da 4ª posição e variação contra o período anterior
              </p>
            </div>
            <p className={classNames(
              'font-instrument text-xs font-semibold text-textSecondary',
              tvMode && 'text-base'
            )}>
              Total no período (demais SDRs):{' '}
              <strong className="text-textPrimary">
                {formatNumber(rankingData?.outside_top_three_count ?? 0)} {copy.plural}
              </strong>
            </p>
          </div>
          <RankingList copy={copy} items={remainingRanking} tvMode={tvMode} />
        </section>
      )}
    </section>
  );

  return tvMode ? createPortal(content, document.body) : content;
});

export default PerformanceRankingTab;
